import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Circle } from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";

const WS_URL = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");

const ChatWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [adminOnline, setAdminOnline] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [chatStarted, setChatStarted] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastDismissed, setToastDismissed] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimer = useRef(null);

  // Show "Need Help?" toast after 5 seconds, auto-hide after 8 more
  useEffect(() => {
    if (toastDismissed || isOpen) return;
    const alreadySeen = sessionStorage.getItem("chat_toast_seen");
    if (alreadySeen) return;
    const showTimer = setTimeout(() => setShowToast(true), 5000);
    const hideTimer = setTimeout(() => {
      setShowToast(false);
      sessionStorage.setItem("chat_toast_seen", "1");
    }, 13000);
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, [toastDismissed, isOpen]);

  // Check admin status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/chat/admin/status`);
        if (res.ok) {
          const data = await res.json();
          setAdminOnline(data.is_online);
        }
      } catch (e) {}
    };
    checkStatus();
    const interval = setInterval(checkStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Reset unread when opening
  useEffect(() => {
    if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  const connectWs = useCallback((sid) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const ws = new WebSocket(`${WS_URL}/api/chat/ws/${sid}`);
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.sender_type) {
        setMessages((prev) => [...prev, msg]);
        if (!isOpen && msg.sender_type === "admin") {
          setUnreadCount((c) => c + 1);
        }
      }
    };
    ws.onclose = () => {
      if (chatStarted) {
        reconnectTimer.current = setTimeout(() => connectWs(sid), 3000);
      }
    };
    wsRef.current = ws;
  }, [chatStarted, isOpen]);

  const startChat = async () => {
    const name = user?.name || guestName || "Visitor";
    const email = user?.email || guestEmail || "";
    if (!user && !guestName.trim()) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/chat/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_name: name, user_email: email, user_id: user?.user_id || "" }),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setChatStarted(true);
      connectWs(data.session_id);

      // Add welcome message
      setMessages([{
        message_id: "welcome",
        sender_type: "system",
        message: adminOnline
          ? "You're connected! Our team will be with you shortly."
          : "We're currently offline. Leave a message and we'll get back to you!",
        timestamp: new Date().toISOString(),
      }]);
    } catch (e) {
      console.error("Failed to start chat:", e);
    }
  };

  const sendMessage = () => {
    if (!input.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: "message",
      message: input.trim(),
      sender_name: user?.name || guestName || "Visitor",
    }));
    setInput("");
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wsRef.current?.close();
      clearTimeout(reconnectTimer.current);
    };
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="chat-widget">
      {/* Chat Window */}
      {isOpen && (
        <div className="mb-3 w-[360px] h-[480px] bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="chat-window">
          {/* Header */}
          <div className="bg-purple-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-white" />
              <span className="text-white font-semibold text-sm">Live Support</span>
              <div className="flex items-center gap-1 ml-2">
                <Circle className={`w-2.5 h-2.5 fill-current ${adminOnline ? "text-green-400" : "text-gray-400"}`} />
                <span className="text-white/70 text-xs">{adminOnline ? "Online" : "Offline"}</span>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white" data-testid="chat-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {!chatStarted ? (
            /* Pre-chat form */
            <div className="flex-1 flex flex-col justify-center px-6 gap-4">
              <div className="text-center">
                <img src="/logo-header.png" alt="DJs&MC Media" className="h-16 w-auto mx-auto mb-2" />
                <h3 className="text-white font-bold text-lg">Chat with us</h3>
                <p className="text-gray-400 text-sm mt-1">
                  {adminOnline ? "We're online and ready to help!" : "Leave us a message, we'll respond soon."}
                </p>
                {!adminOnline && (
                  <div className="flex flex-col items-center gap-1 mt-2">
                    <a href="/guide" className="text-purple-400 hover:text-purple-300 text-xs transition">
                      Check out our Setup Guide while you wait
                    </a>
                    {user && (
                      <a href="/dashboard?tab=support" className="text-purple-400 hover:text-purple-300 text-xs transition">
                        Or submit a Support Ticket
                      </a>
                    )}
                  </div>
                )}
              </div>
              {!user && (
                <>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                    data-testid="chat-guest-name"
                  />
                  <input
                    type="email"
                    placeholder="Email (optional)"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                    data-testid="chat-guest-email"
                  />
                </>
              )}
              {user && (
                <div className="bg-gray-800 rounded-lg px-3 py-2 text-sm">
                  <p className="text-white">{user.name}</p>
                  <p className="text-gray-400 text-xs">{user.email}</p>
                </div>
              )}
              <button
                onClick={startChat}
                disabled={!user && !guestName.trim()}
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition text-sm"
                data-testid="chat-start-btn"
              >
                Start Chat
              </button>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 text-[11px] text-center transition" data-testid="chat-privacy-link">
                Privacy Policy
              </a>
            </div>
          ) : (
            /* Chat messages */
            <>
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="chat-messages">
                {messages.map((msg, i) => (
                  <div key={msg.message_id || i} className={`flex ${msg.sender_type === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                      msg.sender_type === "user"
                        ? "bg-purple-600 text-white rounded-br-sm"
                        : msg.sender_type === "system"
                        ? "bg-gray-800 text-gray-300 text-center text-xs italic w-full max-w-full rounded-lg"
                        : "bg-gray-700 text-white rounded-bl-sm"
                    }`}>
                      {msg.sender_type === "admin" && (
                        <p className="text-purple-400 text-xs font-medium mb-0.5">Support</p>
                      )}
                      <p className="break-words">{msg.message}</p>
                      <p className="text-[10px] opacity-50 mt-0.5">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-gray-700 px-3 py-2 flex gap-2 flex-shrink-0">
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  className="flex-1 bg-gray-800 border border-gray-600 rounded-full px-4 py-2 text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  data-testid="chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim()}
                  className="bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white p-2 rounded-full transition"
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="block text-gray-500 hover:text-gray-300 text-[11px] text-center py-1.5 transition flex-shrink-0" data-testid="chat-privacy-link-active">
                Privacy Policy
              </a>
            </>
          )}
        </div>
      )}

      {/* "Need Help?" toast notification */}
      {showToast && !isOpen && !toastDismissed && (
        <div
          className="mb-3 flex items-center gap-3 bg-gray-900 border border-purple-500/30 rounded-2xl px-4 py-3 shadow-xl shadow-purple-600/10 cursor-pointer transition-all"
          style={{ animation: "chatToastSlide 0.4s ease-out" }}
          onClick={() => { setShowToast(false); setToastDismissed(true); setIsOpen(true); }}
          data-testid="chat-toast"
        >
          <div className="w-10 h-10 rounded-full bg-purple-600/20 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-5 h-5 text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold">Need help?</p>
            <p className="text-gray-400 text-xs">Chat with our support team</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setShowToast(false); setToastDismissed(true); sessionStorage.setItem("chat_toast_seen", "1"); }}
            className="text-gray-500 hover:text-gray-300 p-1 flex-shrink-0"
            data-testid="chat-toast-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Floating bubble */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-full shadow-lg shadow-purple-600/30 transition-all hover:scale-105"
        data-testid="chat-bubble"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {unreadCount > 0 && !isOpen && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
            {unreadCount}
          </span>
        )}
        {/* Online indicator on bubble */}
        <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-gray-900 ${adminOnline ? "bg-green-400" : "bg-gray-500"}`} />
      </button>
    </div>
  );
};

export default ChatWidget;
